import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { ButtonTypes } from "@enums/button-types";
import { ModalTypes } from "@enums/modal-types";
import { ApplicationState } from "@idw-types/state/application-state";
import {
    getSecurityRulesReduxRequest,
    refreshSecurityRulesReduxRequest
} from "@redux-requests/security-rules";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { AddSecurityRuleModal } from "../../modals/add-security-rule-modal/add-security-rule-modal";
import { SecurityRulesTableContainer } from "./security-rules-table-container/security-rules-table-container";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import { CardContainer } from "../../common/card-container/card-container";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { RefreshResultsContainer } from "../refresh-results-container/refresh-results-container";

export const SecurityRulesContainer = () => {
    const modalContext = useModalContext()
    const dispatch = useDispatch();
    const {
        isLoadingAxnAdminSecurityRules,
        axnAdminSecurityRules,
        securityRulesTableState,
        isRefreshing,
        refreshResults,
        lastRefreshDate
    } = useSelector((state: ApplicationState) => ({
        isLoadingAxnAdminSecurityRules: state.securityRules.securityRules.isLoading,
        axnAdminSecurityRules: state.securityRules.securityRules.securityRules,
        securityRulesTableState: state.tableSettings.securityRules,
        isRefreshing: state.securityRules.isRefreshing,
        refreshResults: state.securityRules.refreshResults,
        lastRefreshDate: state.securityRules.lastRefreshDate
    }));

    const [shouldLoadAxnAdminSecurityRules, setShouldLoadAxnAdminSecurityRules] = useState(true);

    useEffect(() => {
        if (!isLoadingAxnAdminSecurityRules && shouldLoadAxnAdminSecurityRules) {
            setShouldLoadAxnAdminSecurityRules(false);
            getSecurityRulesReduxRequest(dispatch)();
        }
    }, [isLoadingAxnAdminSecurityRules, shouldLoadAxnAdminSecurityRules]);

    const isLoading = useMemo(() => {
        return isLoadingAxnAdminSecurityRules;
    }, [isLoadingAxnAdminSecurityRules]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnAdminSecurityRules &&
            !shouldLoadAxnAdminSecurityRules
        );
    }, [isLoading, axnAdminSecurityRules, shouldLoadAxnAdminSecurityRules]);

    const renderAddSecurityRule = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddSecurityRuleModal
                onModalClose={onModalClose}
                securityRules={axnAdminSecurityRules}
            />
        );
    }, [axnAdminSecurityRules]);

    const headerButton = useMemo(() => {
        const onClick = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddSecurityRule,
                renderFunction: renderAddSecurityRule
            });
        };
        return (
            <ButtonWithIcon
                id="add-security-rule-button"
                title={resources.pages.securityRules.headerButton}
                text={resources.pages.securityRules.headerButton}
                onClick={onClick}
                icon={faPlusCircle}
                disabled={isLoading || !isLoaded}
            />
        );
    }, [isLoading, isLoaded, renderAddSecurityRule]);

    const onClickRefreshSecurityRules = () => {
        refreshSecurityRulesReduxRequest(dispatch)();
    };

    const refreshHeaderButton = useMemo(() => {
        return (
            <ButtonWithSpinner
                id="refresh-security-rule-button"
                onClick={onClickRefreshSecurityRules}
                disabled={isRefreshing}
                variant="primary"
                label={resources.common.buttons.refresh.label}
                type={ButtonTypes.BUTTON}
            />
        );
    }, [isRefreshing]);

    return (
        <>
            <CardContainer>
                <ContentPageHeader title={resources.pages.securityRules.header} headerButton={headerButton} />
                <ContentPageBody isMaxWidth={true}>
                    {isLoaded && (
                        <SecurityRulesTableContainer
                            securityRules={axnAdminSecurityRules}
                            tableState={securityRulesTableState}
                        />
                    )}
                    {isLoading && (
                        <LoadingIndicator title={resources.common.loadingIndicatorText} />
                    )}
                </ContentPageBody>
            </CardContainer>
            <CardContainer>
                <ContentPageHeader title={resources.pages.securityRules.refreshHeader} headerButton={refreshHeaderButton} />
                <ContentPageBody isMaxWidth={true}>
                    {!isRefreshing && refreshResults && (
                        <RefreshResultsContainer refreshResults={refreshResults} lastRefreshDate={lastRefreshDate} />
                    )}
                    {isRefreshing && (
                        <LoadingIndicator title={resources.pages.securityRules.loadingIndicatorText} />
                    )}
                </ContentPageBody>
            </CardContainer>
        </>
    );
};